import React, { useContext } from 'react';
import { PROFESSIONAL_QUOTES, QUOTE_THEMES } from '../constants/quotes';
import { useNavigate } from 'react-router-dom';
import { useEmployeeSprintStatus } from '../api/dashboardApi';
import { AuthContext } from '../auth/AuthContext';
import { departmentUsesBugTracker } from '../constants/departments';
import { FiCalendar, FiCheckCircle, FiUsers, FiAlertCircle, FiTrendingUp, FiClock, FiBriefcase, FiFolder } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';

const COLORS = ['#2C8FC7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const ROLE_GOALS = {
  developer: 'Focus on finishing in-progress stories and reducing carryover work.',
  tester: 'Focus on validating high-risk items early and closing verification loops quickly.',
  designer: 'Focus on unblocking handoffs and finalizing priority UI deliverables.',
  devops: 'Focus on delivery stability, deployment readiness, and blocker removal.',
  analyst: 'Focus on clarifying requirements and reducing ambiguity for active tasks.',
  marketing_designer: 'Focus on creative deliverables tied to the active sprint timeline.',
  content_marketer: 'Focus on content tasks in the current sprint and campaign deadlines.',
  sales_marketing: 'Focus on sprint-aligned marketing execution and stakeholder updates.',
  trainee: 'Focus on learning sprint rhythm and closing assigned tasks on time.',
};


const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { role, user } = useContext(AuthContext);
  const { data, isLoading, error, isFetching, refetch } = useEmployeeSprintStatus();

  // Initial load (no prior data and still fetching) → show skeleton
  if (isLoading && !data) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', fontSize: '1.1rem', color: '#64748b' }}>
        Loading your dashboard...
      </div>
    );
  }

  // Hard error AND no cached data — show retryable error wall (no full reload required)
  if (error && !data) {
    return (
      <div style={{ padding: '2rem', background: '#f8fafc', minHeight: '100vh' }}>
        <div style={{
          maxWidth: 600,
          margin: '4rem auto',
          textAlign: 'center',
          padding: '3rem',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#fef3c7',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <FiAlertCircle size={32} color="#f59e0b" />
          </div>

          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.75rem' }}>
            Dashboard Temporarily Unavailable
          </h2>
          <p style={{ color: '#64748b', fontSize: '1rem', margin: '0 0 2rem', lineHeight: 1.6 }}>
            We're having trouble loading your dashboard. This could be a temporary issue.
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #2C8FC7 0%, #1272B3 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: isFetching ? 'wait' : 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: '0 2px 8px rgba(44, 143, 199, 0.3)',
                opacity: isFetching ? 0.7 : 1,
              }}
            >
              {isFetching ? 'Retrying…' : 'Try again'}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#f1f5f9',
                color: '#475569',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              Refresh Page
            </button>
          </div>

          <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '2rem 0 0' }}>
            If this issue persists, please contact your system administrator.
          </p>
        </div>
      </div>
    );
  }

  // Soft warning when we have stale cached data but the latest refetch failed —
  // user keeps seeing their dashboard instead of a full-page error wall.
  const showStaleWarning = !!error && !!data;

  const {
    currentSprint,
    activeSprints = [],
    sprintBurndown,
    myWork,
    myOpenBugs = 0,
    teamProgress,
    blockers,
    lastWeek,
    statusBreakdown,
    projects = [],
  } = data || {};

  const currentRole = (role || '').toLowerCase();
  const showBugKpi = currentRole === 'tester' || departmentUsesBugTracker(user?.department);
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const quoteIndex = (dayOfYear + currentRole.length) % PROFESSIONAL_QUOTES.length;
  const themeIndex = (dayOfYear + currentRole.length) % QUOTE_THEMES.length;
  const dailyQuote = PROFESSIONAL_QUOTES[quoteIndex];
  const quoteTheme = QUOTE_THEMES[themeIndex];
  const assignedCount = Number(myWork?.assigned || 0);
  const completedCount = Number(myWork?.completed || 0);
  const inProgressCount = Number(myWork?.inProgress || 0);
  const pendingCount = Math.max(assignedCount - completedCount - inProgressCount, 0);
  const completionRate = assignedCount > 0 ? Math.round((completedCount / assignedCount) * 100) : 0;
  const sprintActual = Number(currentSprint?.progressActual || 0);
  const sprintExpected = Number(currentSprint?.progressExpected || 0);
  const paceGap = sprintActual - sprintExpected;
  const blockerCount = Number(blockers?.count || 0);
  const criticalBlockers = Number(blockers?.critical || 0);

  const statusCounts = (statusBreakdown || []).reduce((acc, item) => {
    const key = String(item?.status || '').toLowerCase();
    acc[key] = Number(item?.count || 0);
    return acc;
  }, {});

  const laggingStatuses = ['blocked', 'reopened', 'testing', 'in progress'];
  const laggingQueue = laggingStatuses
    .map((status) => ({
      status,
      count: statusCounts[status] || 0,
    }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const myWorkMix = [
    { label: 'Completed', value: completedCount, color: '#10b981' },
    { label: 'In Progress', value: inProgressCount, color: '#2C8FC7' },
    { label: 'Pending', value: pendingCount, color: '#f59e0b' },
  ];

  const roleGoal = ROLE_GOALS[currentRole] || 'Focus on steady delivery, communication, and timely closure of assigned work.';
  const confidenceScore = clamp(
    Math.round(72 + (paceGap * 1.2) + (completionRate * 0.15) - (blockerCount * 4)),
    35,
    98
  );

  const momentumMessage =
    blockerCount === 0 && paceGap >= 0
      ? 'Great momentum. Keep the same pace and continue closing active items.'
      : blockerCount <= 2 && paceGap > -8
      ? 'Progress is healthy. A few focused follow-ups can keep you on track.'
      : 'You are still in a recoverable zone. Prioritize blocker removal and close one high-impact item today.';

  const focusRecommendations = [
    roleGoal,
    blockerCount > 0
      ? `${blockerCount} blocker${blockerCount > 1 ? 's are' : ' is'} open (${criticalBlockers} critical). Follow up with owners and escalate quickly if no movement.`
      : 'No active blockers right now. Keep protecting this by resolving dependencies early.',
    currentSprint
      ? (paceGap < 0
        ? `Sprint pace is ${Math.abs(paceGap)}% behind expected. Shift effort to near-complete tasks to improve closure rate.`
        : `Sprint pace is ${paceGap}% ahead of expected. Keep this consistency and avoid new unplanned work.`)
      : `You have ${assignedCount} assigned item${assignedCount !== 1 ? 's' : ''} across your projects — ${completionRate}% complete so far.`,
    laggingQueue.length > 0
      ? `Lagging queue: ${laggingQueue.slice(0, 3).map(i => `${i.status} (${i.count})`).join(', ')}. Follow up or escalate if items stay unchanged beyond 24 hours.`
      : 'Lagging queue is under control. Continue regular daily status updates with your team.',
  ];

  const timelineColor = currentSprint?.timelineStatus === 'onTrack' ? '#10b981' : '#ef4444';
  const timelineText = currentSprint?.timelineStatus === 'onTrack' ? 'On Track' : 'Behind Schedule';
  const timelineIcon = currentSprint?.timelineStatus === 'onTrack' ? '✓' : '⚠';
  const otherActiveSprints = activeSprints.filter((s) => s.id !== currentSprint?.id);

  if (projects.length === 0) {
    return (
      <div style={{ padding: '2rem', background: '#f8fafc', minHeight: '100vh' }}>
        <div style={{
          maxWidth: 640,
          margin: '4rem auto',
          textAlign: 'center',
          padding: '3rem',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <FiFolder size={48} color="#94a3b8" style={{ marginBottom: '1rem' }} />
          <h2 style={{ margin: '0 0 0.75rem', color: '#1e293b' }}>No projects assigned yet</h2>
          <p style={{ color: '#64748b', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
            Once you are added to a project, your tasks, sprint progress, and project status will appear here.
          </p>
          <button
            type="button"
            onClick={() => navigate('/my-tasks')}
            style={{
              padding: '0.7rem 1.1rem',
              background: '#1272B3',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Open My Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', background: '#f8fafc', minHeight: '100vh' }}>

      {showStaleWarning && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          color: '#92400e',
          borderRadius: 12,
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}>
          <span>Showing the last loaded data — couldn't refresh just now.</span>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            style={{
              padding: '0.4rem 0.85rem',
              background: '#fff',
              color: '#92400e',
              border: '1px solid #fbbf24',
              borderRadius: 6,
              cursor: isFetching ? 'wait' : 'pointer',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {/* 0. DAILY QUOTE — FIRST ITEM */}
      <div style={{
        background: quoteTheme.bg,
        border: `1px solid ${quoteTheme.border}`,
        borderRadius: 14,
        padding: '0.95rem 1.1rem',
        marginBottom: '1.5rem',
        color: quoteTheme.text,
        fontSize: '0.92rem',
        fontWeight: 600,
        lineHeight: 1.5,
        fontStyle: 'italic'
      }}>
        "{dailyQuote}"
      </div>

      {/* 1. SPRINT CONTEXT */}
      {currentSprint ? (
      <div style={{
        background: 'linear-gradient(135deg, #1272B3 0%, #2C8FC7 100%)',
        borderRadius: 20,
        padding: '2rem',
        color: '#fff',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px rgba(18, 114, 179, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.5rem' }}>
              <FiCalendar size={24} />
              <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>My Active Sprint</span>
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 0.5rem' }}>
              {currentSprint.name}
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
              {currentSprint.projectName}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.25rem' }}>Days Remaining</div>
              <div style={{ fontSize: '2.5rem', fontWeight: 800 }}>{currentSprint.daysRemaining}</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>of {currentSprint.totalDays} days</div>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.25rem' }}>Team Progress</div>
              <div style={{ fontSize: '2.5rem', fontWeight: 800 }}>{currentSprint.progressActual}%</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Expected: {currentSprint.progressExpected}%</div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {teamProgress.completedPoints} / {teamProgress.totalPoints} Story Points
            </span>
            <span style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              padding: '2px 10px',
              borderRadius: 12,
              background: timelineColor,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {timelineIcon} {timelineText}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: 12,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 6,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${currentSprint.progressActual}%`,
              height: '100%',
              background: timelineColor,
              borderRadius: 6,
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>
      ) : (
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem',
        }}>
          <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.6 }}>
            You are assigned to <strong>{projects.length}</strong> project{projects.length !== 1 ? 's' : ''}.
            {activeSprints.length > 0
              ? ' Pick up sprint tasks from the board — active sprints are running in your projects below.'
              : ' No active sprint is running in your projects right now.'}
          </p>
        </div>
      )}

      {(otherActiveSprints.length > 0 || (!currentSprint && activeSprints.length > 0)) && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: '0 0 1rem' }}>
            {currentSprint ? 'Other Active Sprints' : 'Active Sprints in Your Projects'}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem',
          }}>
            {(currentSprint ? otherActiveSprints : activeSprints).map((sprint) => {
              const sprintTimelineColor = sprint.timelineStatus === 'onTrack' ? '#10b981' : '#ef4444';
              return (
                <div key={sprint.id} style={{
                  ...chartCardStyle,
                  padding: '1.15rem',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/sprint-board')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/sprint-board')}
                role="button"
                tabIndex={0}
                >
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                    {sprint.projectName}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                    {sprint.name}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 10 }}>
                    <span>{sprint.daysRemaining} days left</span>
                    <span style={{ color: sprintTimelineColor, fontWeight: 700 }}>
                      {sprint.progressActual}% team progress
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                    {sprint.myAssigned > 0
                      ? `${sprint.myAssigned} task${sprint.myAssigned !== 1 ? 's' : ''} assigned to you`
                      : 'No tasks assigned to you yet'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. KPI CARDS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>My Work</div>
              <div style={kpiValueStyle}>{myWork?.assigned ?? 0}</div>
              <div style={kpiSubtextStyle}>
                {myWork?.completed ?? 0} done • {myWork?.inProgress ?? 0} in progress
                {(myWork?.testing ?? 0) > 0 ? ` • ${myWork.testing} in testing` : ''}
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: '#EAF6FD' }}>
              <FiCheckCircle size={24} color="#1272B3" />
            </div>
          </div>
        </div>

        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>My Completion</div>
              <div style={kpiValueStyle}>{completionRate}%</div>
              <div style={kpiSubtextStyle}>
                {currentSprint ? 'In your active sprint' : 'Across assigned projects'}
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: '#fef3c7' }}>
              <FiTrendingUp size={24} color="#d97706" />
            </div>
          </div>
        </div>

        {currentSprint ? (
        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>Team Progress</div>
              <div style={kpiValueStyle}>{teamProgress?.teamMembers ?? 0}</div>
              <div style={kpiSubtextStyle}>
                {teamProgress?.completedPoints ?? 0} of {teamProgress?.totalPoints ?? 0} pts
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: '#d1fae5' }}>
              <FiUsers size={24} color="#10b981" />
            </div>
          </div>
        </div>
        ) : (
        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>My Projects</div>
              <div style={kpiValueStyle}>{projects.length}</div>
              <div style={kpiSubtextStyle}>
                {activeSprints.length} active sprint{activeSprints.length !== 1 ? 's' : ''} running
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: '#e0e7ff' }}>
              <FiBriefcase size={24} color="#4338ca" />
            </div>
          </div>
        </div>
        )}

        {showBugKpi && (
        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>My Open Bugs</div>
              <div style={{ ...kpiValueStyle, color: myOpenBugs > 0 ? '#dc2626' : '#10b981' }}>
                {myOpenBugs}
              </div>
              <div style={kpiSubtextStyle}>
                {currentSprint ? 'In your active sprint' : 'Assigned to you'}
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: myOpenBugs > 0 ? '#fee2e2' : '#d1fae5' }}>
              <FiAlertCircle size={24} color={myOpenBugs > 0 ? '#dc2626' : '#10b981'} />
            </div>
          </div>
        </div>
        )}

        <div style={kpiCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={kpiLabelStyle}>{currentSprint ? 'Sprint Blockers' : 'My Blockers'}</div>
              <div style={{ ...kpiValueStyle, color: blockers?.count > 0 ? '#dc2626' : '#10b981' }}>
                {blockers?.count ?? 0}
              </div>
              <div style={kpiSubtextStyle}>
                {blockers?.critical ?? 0} critical
              </div>
            </div>
            <div style={{ ...kpiIconStyle, background: blockers?.count > 0 ? '#fee2e2' : '#d1fae5' }}>
              <FiAlertCircle size={24} color={blockers?.count > 0 ? '#dc2626' : '#10b981'} />
            </div>
          </div>
        </div>
      </div>

      {/* 3. MY PROJECTS */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: '0 0 1rem' }}>
          My Projects
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1rem',
        }}>
          {projects.map((project) => (
            <div key={project.id} style={chartCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.98rem' }}>{project.name}</div>
                <span style={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: project.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                  color: project.status === 'Active' ? '#166534' : '#64748b',
                }}>
                  {project.status || 'Unknown'}
                </span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 10 }}>
                {project.activeSprints > 0 ? (
                  <>
                    {project.completedIssues} / {project.totalIssues} active sprint tasks done
                    {project.activeSprintName && (
                      <span style={{ display: 'block', marginTop: 4, color: '#475569', fontWeight: 600 }}>
                        {project.activeSprintName}
                      </span>
                    )}
                  </>
                ) : (
                  'No active sprint running'
                )}
              </div>
              <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  width: `${project.activeSprints > 0 ? project.progress : 0}%`,
                  height: '100%',
                  background: project.progress >= 70 ? '#10b981' : '#2C8FC7',
                  borderRadius: 999,
                  opacity: project.activeSprints > 0 ? 1 : 0.35,
                }} />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 6, fontWeight: 600 }}>
                {project.activeSprints > 0 ? `${project.progress}% sprint complete` : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4. SMART REVIEW + MY WORK GRAPH */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={chartCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <h3 style={chartTitleStyle}>Smart Review (Phase 1)</h3>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.3rem' }}>
                Motivational guidance based on sprint progress and role focus
              </div>
            </div>
            <div style={{
              padding: '0.35rem 0.65rem',
              borderRadius: 999,
              background: confidenceScore >= 70 ? '#dcfce7' : '#fef3c7',
              color: confidenceScore >= 70 ? '#166534' : '#92400e',
              fontWeight: 700,
              fontSize: '0.78rem'
            }}>
              Confidence {confidenceScore}%
            </div>
          </div>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: '0.9rem',
            marginBottom: '1rem',
            color: '#334155',
            fontWeight: 600,
            fontSize: '0.9rem',
            lineHeight: 1.5
          }}>
            {momentumMessage}
          </div>

          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {focusRecommendations.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: '0.86rem',
                color: '#475569',
                lineHeight: 1.45
              }}>
                <span style={{ color: '#1272B3', fontWeight: 800 }}>•</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>My Workload Snapshot</h3>
          <div style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.35rem 0 0.8rem' }}>
            Quick visual on completed, active, and pending work
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.6rem', color: '#64748b', fontSize: '0.82rem' }}>
            <FiClock size={15} />
            Completion rate: {completionRate}%
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={myWorkMix} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {myWorkMix.map((entry) => <Cell key={entry.label} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 5. CHARTS GRID */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>

        {currentSprint && sprintBurndown?.length > 0 && (
        <div style={{ ...chartCardStyle, gridColumn: 'span 1' }}>
          <h3 style={chartTitleStyle}>Sprint Burndown</h3>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
            Ideal vs Actual Progress
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={sprintBurndown} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval={Math.floor(sprintBurndown.length / 8)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="ideal" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Ideal" dot={false} />
              <Line type="monotone" dataKey="actual" stroke="#1272B3" strokeWidth={3} name="Actual" dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
        )}

        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>{currentSprint ? 'My Sprint Status' : 'My Task Status'}</h3>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
            Work Distribution
          </div>
          {statusBreakdown && statusBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusBreakdown}
                  cx="50%"
                  cy="50%"
                  dataKey="count"
                  nameKey="status"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  label={({ status, count }) => `${status}: ${count}`}
                  labelStyle={{ fontSize: 11, fontWeight: 600 }}
                >
                  {statusBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
              No status data available
            </div>
          )}
        </div>
      </div>

      {/* 6. LAST WEEK + QUICK ACTIONS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap: '1.5rem',
      }}>
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>{currentSprint ? 'Team Last Week' : 'My Last Week'}</h3>
          <div style={{ padding: '1rem 0' }}>
            <div style={statRowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiCheckCircle size={18} color="#10b981" />
                <span style={{ fontWeight: 600, color: '#475569' }}>Completed Tasks</span>
              </div>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
                {lastWeek.completedTasks}
              </span>
            </div>
            <div style={statRowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiTrendingUp size={18} color="#2C8FC7" />
                <span style={{ fontWeight: 600, color: '#475569' }}>Story Points</span>
              </div>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
                {lastWeek.completedPoints}
              </span>
            </div>
            <div style={statRowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiUsers size={18} color="#8b5cf6" />
                <span style={{ fontWeight: 600, color: '#475569' }}>
                  {currentSprint ? 'Active Members' : 'Projects Touched'}
                </span>
              </div>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
                {currentSprint ? lastWeek.activeMembers : projects.length}
              </span>
            </div>
          </div>
        </div>

        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', paddingTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => navigate('/my-tasks')}
              style={quickActionBtnStyle}
            >
              Open My Tasks
            </button>
            <button
              type="button"
              onClick={() => navigate('/sprint-board')}
              style={quickActionBtnStyle}
            >
              Open Sprint Board
            </button>
            {showBugKpi && (
              <button
                type="button"
                onClick={() => navigate('/issues')}
                style={quickActionBtnStyle}
              >
                Open Issues
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const kpiCardStyle = {
  background: '#fff',
  padding: '1.5rem',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
};

const kpiLabelStyle = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#64748b',
  marginBottom: '0.5rem'
};

const kpiValueStyle = {
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#1e293b',
  lineHeight: 1
};

const kpiSubtextStyle = {
  fontSize: '0.8rem',
  color: '#94a3b8',
  marginTop: '0.5rem'
};

const kpiIconStyle = {
  width: 56,
  height: 56,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const chartCardStyle = {
  background: '#fff',
  padding: '1.5rem',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
};

const chartTitleStyle = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#1e293b',
  margin: 0
};

const statRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 0',
  borderBottom: '1px solid #f1f5f9'
};

const quickActionBtnStyle = {
  padding: '0.75rem 1rem',
  background: '#1272B3',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.88rem',
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'left',
};

export default EmployeeDashboard;
